import { state, frameToSeconds } from '../state.ts';
import { evalInfluence, LIMB_LABELS, type ContactLimb, type ContactPin } from '../retarget/contacts.ts';

export interface ContactsPanelHandlers {
  /** Actors that can be pinned (have a puppet) / targeted. */
  getActors: () => { id: string; label: string }[];
  onAddPin: () => void;
  onSelectPin: (id: string) => void;
  onDeletePin: (id: string) => void;
  onPinChange: (
    id: string,
    patch: {
      actorId?: string;
      limb?: ContactLimb;
      targetKind?: 'point' | 'bone';
      point?: [number, number, number];
      targetActorId?: string;
      targetBone?: string;
      enabled?: boolean;
      targetOffset?: [number, number, number];
      matchRotation?: boolean;
      targetRotate?: [number, number, number];
    }
  ) => void;
  /** Add/update an influence key at the playhead (value 0..1). */
  onSetKey: (id: string, value: number) => void;
  onDeleteKey: (id: string, index: number) => void;
}

/**
 * Sidebar "Contact pins" section: list of pins, and an editor for the selected
 * pin (actor, limb, target, influence keys). Rendered from state on update().
 */
export function createContactsPanel(handlers: ContactsPanelHandlers) {
  const panel = byId<HTMLElement>('contacts-panel');
  const list = byId<HTMLDivElement>('contact-list');
  const addBtn = byId<HTMLButtonElement>('contact-add');
  const editor = byId<HTMLDivElement>('contact-editor');
  const enabledToggle = byId<HTMLInputElement>('contact-enabled');
  const actorSelect = byId<HTMLSelectElement>('contact-actor');
  const limbSelect = byId<HTMLSelectElement>('contact-limb');
  const targetKind = byId<HTMLSelectElement>('contact-target-kind');
  const pointRow = byId<HTMLDivElement>('contact-point-row');
  const boneRow = byId<HTMLDivElement>('contact-bone-row');
  const pointX = byId<HTMLInputElement>('contact-x');
  const pointY = byId<HTMLInputElement>('contact-y');
  const pointZ = byId<HTMLInputElement>('contact-z');
  const targetActor = byId<HTMLSelectElement>('contact-target-actor');
  const targetBone = byId<HTMLSelectElement>('contact-target-bone');
  const offX = byId<HTMLInputElement>('contact-off-x');
  const offY = byId<HTMLInputElement>('contact-off-y');
  const offZ = byId<HTMLInputElement>('contact-off-z');
  const matchRot = byId<HTMLInputElement>('contact-match-rot');
  const rotRow = byId<HTMLDivElement>('contact-rot-row');
  const rotX = byId<HTMLInputElement>('contact-rot-x');
  const rotY = byId<HTMLInputElement>('contact-rot-y');
  const rotZ = byId<HTMLInputElement>('contact-rot-z');
  const influence = byId<HTMLInputElement>('contact-influence');
  const influenceValue = byId<HTMLOutputElement>('contact-influence-value');
  const keyAdd = byId<HTMLButtonElement>('contact-key-add');
  const deleteBtn = byId<HTMLButtonElement>('contact-delete');
  const keysList = byId<HTMLDivElement>('contact-keys');

  let lastSignature = '';

  addBtn.addEventListener('click', () => handlers.onAddPin());
  deleteBtn.addEventListener('click', () => {
    if (state.selectedContactId) handlers.onDeletePin(state.selectedContactId);
  });

  const selected = (): ContactPin | null =>
    state.contacts.find((p) => p.id === state.selectedContactId) ?? null;

  enabledToggle.addEventListener('change', () => {
    const pin = selected();
    if (pin) handlers.onPinChange(pin.id, { enabled: enabledToggle.checked });
  });
  actorSelect.addEventListener('change', () => {
    const pin = selected();
    if (pin) handlers.onPinChange(pin.id, { actorId: actorSelect.value });
  });
  limbSelect.addEventListener('change', () => {
    const pin = selected();
    if (pin) handlers.onPinChange(pin.id, { limb: limbSelect.value as ContactLimb });
  });
  targetKind.addEventListener('change', () => {
    const pin = selected();
    if (pin) handlers.onPinChange(pin.id, { targetKind: targetKind.value as 'point' | 'bone' });
  });
  const emitPoint = (): void => {
    const pin = selected();
    if (pin) {
      handlers.onPinChange(pin.id, {
        point: [Number(pointX.value) || 0, Number(pointY.value) || 0, Number(pointZ.value) || 0],
      });
    }
  };
  for (const input of [pointX, pointY, pointZ]) input.addEventListener('input', emitPoint);
  targetActor.addEventListener('change', () => {
    const pin = selected();
    if (pin) handlers.onPinChange(pin.id, { targetActorId: targetActor.value });
  });
  targetBone.addEventListener('change', () => {
    const pin = selected();
    if (pin) handlers.onPinChange(pin.id, { targetBone: targetBone.value });
  });
  const emitTargetOffset = (): void => {
    const pin = selected();
    if (pin) {
      handlers.onPinChange(pin.id, {
        targetOffset: [Number(offX.value) || 0, Number(offY.value) || 0, Number(offZ.value) || 0],
      });
    }
  };
  for (const input of [offX, offY, offZ]) input.addEventListener('input', emitTargetOffset);
  matchRot.addEventListener('change', () => {
    const pin = selected();
    if (pin) handlers.onPinChange(pin.id, { matchRotation: matchRot.checked });
  });
  const emitTargetRotate = (): void => {
    const pin = selected();
    if (pin) {
      handlers.onPinChange(pin.id, {
        targetRotate: [Number(rotX.value) || 0, Number(rotY.value) || 0, Number(rotZ.value) || 0],
      });
    }
  };
  for (const input of [rotX, rotY, rotZ]) input.addEventListener('input', emitTargetRotate);

  influence.addEventListener('input', () => {
    influenceValue.value = influence.value;
  });
  keyAdd.addEventListener('click', () => {
    if (state.selectedContactId) {
      handlers.onSetKey(state.selectedContactId, Number(influence.value) / 100);
    }
  });

  function pinLabel(pin: ContactPin): string {
    const actor = handlers.getActors().find((a) => a.id === pin.actorId);
    const what = `${actor?.label ?? '?'} · ${LIMB_LABELS[pin.limb]}`;
    return pin.target.kind === 'point' ? `${what} → point` : `${what} → bone`;
  }

  function rebuildList(): void {
    list.innerHTML = '';
    for (const pin of state.contacts) {
      const row = document.createElement('div');
      row.className = 'camera-row';
      row.classList.toggle('selected', pin.id === state.selectedContactId);
      const name = document.createElement('span');
      name.className = 'actor-name';
      name.textContent = pinLabel(pin);
      row.appendChild(name);
      row.addEventListener('click', () => handlers.onSelectPin(pin.id));
      list.appendChild(row);
    }
  }

  function rebuildKeys(pin: ContactPin): void {
    keysList.innerHTML = '';
    pin.keys.forEach((k, index) => {
      const row = document.createElement('div');
      row.className = 'contact-key-row';
      const label = document.createElement('span');
      label.textContent = `${k.time.toFixed(2)}s → ${Math.round(k.value * 100)}%`;
      const del = document.createElement('button');
      del.type = 'button';
      del.className = 'btn icon small';
      del.innerHTML = '&times;';
      del.title = 'Delete key';
      del.addEventListener('click', () => handlers.onDeleteKey(pin.id, index));
      row.append(label, del);
      keysList.appendChild(row);
    });
  }

  /** Reflect state (called on every notify()). */
  function update(): void {
    const actors = handlers.getActors();
    panel.hidden = actors.length === 0 && state.contacts.length === 0;
    if (panel.hidden) return;
    addBtn.disabled = actors.length === 0;

    const pin = selected();
    editor.hidden = !pin;

    const signature = JSON.stringify([
      state.contacts,
      state.selectedContactId,
      actors.map((a) => a.id),
    ]);
    if (signature !== lastSignature) {
      lastSignature = signature;
      rebuildList();

      if (pin) {
        enabledToggle.checked = pin.enabled;
        // (Re)fill actor dropdowns.
        for (const select of [actorSelect, targetActor]) {
          select.innerHTML = '';
          for (const a of actors) {
            const opt = document.createElement('option');
            opt.value = a.id;
            opt.textContent = a.label;
            select.appendChild(opt);
          }
        }
        actorSelect.value = pin.actorId;
        limbSelect.value = pin.limb;
        targetKind.value = pin.target.kind;
        pointRow.hidden = pin.target.kind !== 'point';
        boneRow.hidden = pin.target.kind !== 'bone';
        if (pin.target.kind === 'point') {
          if (document.activeElement !== pointX) pointX.value = String(pin.target.point[0]);
          if (document.activeElement !== pointY) pointY.value = String(pin.target.point[1]);
          if (document.activeElement !== pointZ) pointZ.value = String(pin.target.point[2]);
        } else {
          targetActor.value = pin.target.actorId;
          targetBone.value = pin.target.bone;
          const off = pin.target.offset ?? [0, 0, 0];
          if (document.activeElement !== offX) offX.value = String(off[0]);
          if (document.activeElement !== offY) offY.value = String(off[1]);
          if (document.activeElement !== offZ) offZ.value = String(off[2]);
          matchRot.checked = pin.target.matchRotation === true;
          rotRow.hidden = !matchRot.checked;
          const rot = pin.target.rotate ?? [0, 0, 0];
          if (document.activeElement !== rotX) rotX.value = String(rot[0]);
          if (document.activeElement !== rotY) rotY.value = String(rot[1]);
          if (document.activeElement !== rotZ) rotZ.value = String(rot[2]);
        }
        rebuildKeys(pin);
      }
    }

    // The influence slider tracks the evaluated value at the playhead unless
    // the user is holding it.
    if (pin && document.activeElement !== influence) {
      const value = Math.round(evalInfluence(pin.keys, frameToSeconds(state.currentFrame)) * 100);
      influence.value = String(value);
      influenceValue.value = String(value);
    }
  }

  return { update };
}

function byId<T extends HTMLElement>(id: string): T {
  const el = document.getElementById(id);
  if (!el) throw new Error(`Missing element #${id}`);
  return el as T;
}
